import { Button } from "@/src/components/buttons";
import { Card, CheckboxCard } from "@/src/components/cards";
import { closeDrawer, openDrawer } from "@/src/components/dialogs";
import { InfiniteFetchableList } from "@/src/components/fetchableLists";
import { useEffect, useState } from "react";
import { FiEdit2 } from "react-icons/fi";
import { getItemId, openToast, uid } from "../utils/utils";

type LoadItemsType<T> = (
  limit: number,
  offset: number,
  searchKey: string
) => Promise<(T | null | undefined)[] | null | undefined>;

export function SinglePicker<T>({
  title,
  value,
  queryKey,
  onAddClick,
  getChecboxCardContent,
  getChild,
  loadItems,
  onChange,
}: {
  title: string;
  value: T | null | undefined;
  queryKey: any[];
  onAddClick?: (searchKey: string, drawerId: string) => void;

  getChecboxCardContent: (item: T) => React.ReactNode;
  loadItems: LoadItemsType<T>;
  onChange: (newValue: T | null | undefined) => void;
  getChild: (item: T) => React.ReactNode;
}) {
  const [localValue, setLocalValue] = useState(value);
  function onLocalChange(newValue: T | null | undefined) {
    setLocalValue(newValue);
    onChange(newValue);
  }

  async function onClick() {
    await openSinglePickerList({
      title,
      queryKey,
      onAddClick,
      getChecboxCardContent,
      loadItems,
      current: localValue,
      onSelect: (newValue) => {
        onLocalChange(newValue);
      },
    });
  }
  return (
    <div className="c1 gap5">
      <div className="c1-auto acenter">
        <div className="label ">{title}</div>
        {localValue != null && (
          <FiEdit2 className="pointer" size={18} onClick={onClick} />
        )}
      </div>
      <div
        className="c1 gap10"
        onClick={localValue != null ? onClick : undefined}
      >
        {localValue != null && getChild(localValue)}

        {localValue == null && (
          <Card
            color="simple"
            onClick={async () => {
              await onClick();
            }}
          >
            <div className="">Ajouter {title}</div>
          </Card>
        )}
      </div>
    </div>
  );
}

export function SinglePickerList<T extends any>({
  queryKey,
  shouldSelectFirst,
  drawerId,
  currentSearchKey,
  disableSearch,
  onAddClick,
  loadItems,
  nullTitle,
  getChecboxCardContent,
  current,
  onChange,
  limit,
  alreadyAdded,
}: {
  queryKey: any[];
  shouldSelectFirst?: boolean;
  disableSearch?: boolean;
  drawerId?: string;
  currentSearchKey?: string | null | undefined;
  nullTitle?: string | null | undefined;
  loadItems: LoadItemsType<T>;
  onAddClick?: (searchKey: string, drawerId: string) => void;
  getChecboxCardContent: (item: T) => React.ReactNode;
  current: T | null | undefined;
  onChange: (newValue: T | null | undefined) => void;
  limit?: number;
  alreadyAdded?: (T | null | undefined)[] | null | undefined;
}) {
  const [reload, setReload] = useState(0);
  const [value, setValue] = useState(current);
  const [searchKey, setSearchKey] = useState(currentSearchKey || "");
  const [viewType, setViewType] = useState<"all" | "selected">("all");

  return (
    <div className="c1 gap15">
      <InfiniteFetchableList
        queryKey={[...queryKey, searchKey, reload, viewType]}
        getFunction={(limit, offset, searchKey) =>
          loadItems(limit, offset, searchKey || "")
        }
        limit={limit}
        shouldFocus={true}
        searchKey={searchKey}
        onSearch={
          disableSearch
            ? undefined
            : (newValue) => {
                setSearchKey(newValue || "");
              }
        }
        onClick={
          onAddClick ? () => onAddClick(searchKey, drawerId || "") : undefined
        }
        emptyChild={
          <div className="center c1 gap10 p20">
            {nullTitle && (
              <CheckboxCard
                key={uid()}
                color="simple"
                title={nullTitle}
                status={value == null ? "active" : "inactive"}
                onClick={async () => {
                  setValue(null);
                  await onChange(null);
                }}
              />
            )}
            <div className="gray">Aucun élément trouvé</div>
            {onAddClick && (
              <Button
                type="tertiary"
                onClick={() => onAddClick(searchKey, drawerId || "")}
              >
                Ajouter
              </Button>
            )}
          </div>
        }
      >
        {(data) => {
          return (
            <SinglePickerListContent
              data={data}
              nullTitle={nullTitle}
              shouldSelectFirst={shouldSelectFirst}
              currentValue={value}
              onChange={(newValue) => {
                setValue(newValue);
                onChange(newValue);
              }}
              getChecboxCardContent={getChecboxCardContent}
              alreadyAdded={alreadyAdded}
            />
          );
        }}
      </InfiniteFetchableList>
    </div>
  );
}

export function SinglePickerListContent<T>({
  data,
  nullTitle,
  shouldSelectFirst,
  currentValue,
  onChange,
  getChecboxCardContent,
  alreadyAdded,
}: {
  data: (T | null | undefined)[] | null | undefined;
  nullTitle?: string | null | undefined;
  shouldSelectFirst?: boolean;
  currentValue: T | null | undefined;
  onChange: (newValue: T | null | undefined) => void;
  getChecboxCardContent: (item: T) => React.ReactNode;
  alreadyAdded: (T | null | undefined)[] | null | undefined;
}) {
  const [value, setValue] = useState(currentValue);

  useEffect(() => {
    if (shouldSelectFirst && !value && data?.[0]) {
      setValue(data[0]);
      onChange(data[0]);
    }

    const handleKeyUp = (e: KeyboardEvent) => {
      console.log("handleKeyUp", e.key);
      let currentSelectedIndex =
        data?.findIndex((item) => getItemId(item) == getItemId(value)) || 0;
      let newIndex = null;
      if (e.key === "ArrowDown") {
        console.log(
          "currentSelectedIndex",
          currentSelectedIndex,
          data?.length || 0
        );
        if (currentSelectedIndex >= (data?.length || 0) - 1) return;
        newIndex = currentSelectedIndex + 1;
      } else if (e.key === "ArrowUp") {
        newIndex = currentSelectedIndex - 1;
      }

      if (newIndex == null) return;

      let newValue = data?.[newIndex];
      setValue(newValue);
      onChange(newValue);
    };

    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [JSON.stringify(data), value]);

  return (
    <div className={`c1 gap10`}>
      {nullTitle && (
        <CheckboxCard
          key={uid()}
          color="simple"
          title={nullTitle}
          status={value == null ? "active" : "inactive"}
          onClick={async () => {
            setValue(null);
            await onChange(null);
          }}
        />
      )}
      {data?.map((item) => {
        if (!item) return null;
        let content = getChecboxCardContent(item);
        if (!content) return null;
        const isAlreadyAdded = alreadyAdded?.some(
          (added) => getItemId(added) == getItemId(item)
        );

        return (
          <CheckboxCard
            key={uid()}
            color={
              getItemId(value) == getItemId(item) || isAlreadyAdded
                ? "blue"
                : "simple"
            }
            title={null}
            // className={isAlreadyAdded ? "disabled" : ""}
            status={
              getItemId(value) == getItemId(item) || isAlreadyAdded
                ? "active"
                : "inactive"
            }
            onClick={async () => {
              try {
                if (isAlreadyAdded)
                  throw new Error("Cet élement est Déjà ajouté");

                if (getItemId(value) == getItemId(item)) {
                  setValue(null);
                  await onChange(null);
                } else {
                  setValue(item);
                  await onChange(item);
                }
              } catch (e) {
                openToast("error", (e as any).message || "");
                setValue(null);
              }
            }}
          >
            <div className="c1 ">
              {isAlreadyAdded && (
                <div className="gray subTitle">Déjà ajouté</div>
              )}
              {content}
            </div>
          </CheckboxCard>
        );
      })}
    </div>
  );
}

export async function openSinglePickerList<T>({
  title,
  searchKey,
  queryKey,
  loadItems,
  getChecboxCardContent,
  alreadyAdded,
  current,
  onSelect,
  onAddClick,
}: {
  title: string;
  queryKey: any[];
  searchKey?: string | null | undefined;
  loadItems: LoadItemsType<T>;
  getChecboxCardContent: (item: T) => React.ReactNode;
  alreadyAdded?: (T | null | undefined)[] | null | undefined;
  current: T | null | undefined;
  onSelect: (newValue: T | null | undefined) => void;
  onAddClick?: (searchKey: string, drawerId: string) => void;
}) {
  let value = current;
  const drawerId = uid();
  return await openDrawer({
    id: drawerId,
    title: `${title}`,
    content: (
      <SinglePickerList
        drawerId={drawerId}
        queryKey={queryKey}
        alreadyAdded={alreadyAdded}
        onAddClick={(searchKey) => {
          if (onAddClick) onAddClick(searchKey, drawerId);
        }}
        loadItems={loadItems}
        currentSearchKey={searchKey}
        getChecboxCardContent={getChecboxCardContent}
        current={value}
        onChange={async (newValue) => {
          await onSelect(newValue);
          closeDrawer(drawerId);
        }}
      />
    ),
  });
}
